import { useAuth } from "@/_core/hooks/useAuth";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { trpc } from "@/lib/trpc";
import { Check, ChevronsUpDown, Eye, User, Users } from "lucide-react";
import { createContext, useCallback, useContext, useMemo, useState } from "react";

/** Sentinel: visão consolidada de todos os afiliados (modo "Todos"). */
export const ALL_AFFILIATES_ID = "__all__";

// ─── Context for selected affiliate across pages ─────────────────────────

interface AffiliateOption {
  id: string;
  name: string;
  email: string;
  status: string;
  manager_name?: string | null;
}

interface AffiliateSelectionContextValue {
  selectedId: string | undefined;
  setSelectedId: (id: string | undefined) => void;
  selectedAffiliate: AffiliateOption | undefined;
  affiliates: AffiliateOption[];
  isLoading: boolean;
  isManager: boolean; // true if user is admin/diretoria/gestao (can switch)
}

const AffiliateSelectionContext = createContext<AffiliateSelectionContextValue>({
  selectedId: undefined,
  setSelectedId: () => {},
  selectedAffiliate: undefined,
  affiliates: [],
  isLoading: false,
  isManager: false,
});

export function useAffiliateSelection() {
  return useContext(AffiliateSelectionContext);
}

/**
 * Provider that wraps partner pages. Fetches available affiliates and manages selection state.
 */
export function AffiliateSelectionProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const role = user?.role || "";
  const isManager = ["admin", "diretoria", "gestao", "affiliate_manager"].includes(role);

  const { data: affiliates = [], isLoading } = trpc.partner.listAvailable.useQuery(undefined, {
    staleTime: 5 * 60 * 1000,
  });

  // For affiliates, auto-select self. For managers, start with none (prompt to select).
  const [selectedId, setSelectedId] = useState<string | undefined>(undefined);

  // Auto-select for affiliate role (only one option)
  const effectiveSelectedId = useMemo(() => {
    if (role === "affiliate" && affiliates.length === 1 && !selectedId) {
      return affiliates[0].id;
    }
    return selectedId;
  }, [role, affiliates, selectedId]);

  const selectedAffiliate = useMemo(
    () => affiliates.find((a) => a.id === effectiveSelectedId),
    [affiliates, effectiveSelectedId],
  );

  const value = useMemo(
    () => ({
      selectedId: effectiveSelectedId,
      setSelectedId,
      selectedAffiliate,
      affiliates,
      isLoading,
      isManager,
    }),
    [effectiveSelectedId, selectedAffiliate, affiliates, isLoading, isManager],
  );

  return (
    <AffiliateSelectionContext.Provider value={value}>
      {children}
    </AffiliateSelectionContext.Provider>
  );
}

/**
 * Dropdown selector component — looks like Facebook/Google Ads account picker.
 * Shows at the top of partner pages for managers.
 */
export function AffiliateSelector() {
  const { selectedId, setSelectedId, selectedAffiliate, affiliates, isLoading, isManager } =
    useAffiliateSelection();
  const [open, setOpen] = useState(false);

  // Don't show selector for affiliate role (they only see their own data)
  if (!isManager) return null;

  return (
    <div className="flex items-center gap-3 rounded-lg border border-border bg-card p-3 mb-4">
      <div className="flex items-center gap-2 text-sm text-muted-foreground shrink-0">
        <Eye className="h-4 w-4" />
        <span className="font-medium">Visualizar como:</span>
      </div>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full max-w-sm justify-between font-normal"
          >
            {isLoading ? (
              <span className="text-muted-foreground">Carregando...</span>
            ) : selectedId === ALL_AFFILIATES_ID ? (
              <div className="flex items-center gap-2 truncate">
                <Users className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                <span className="truncate">Todos os afiliados</span>
              </div>
            ) : selectedAffiliate ? (
              <div className="flex items-center gap-2 truncate">
                <User className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                <span className="truncate">{selectedAffiliate.name}</span>
                <Badge
                  variant={selectedAffiliate.status === "active" ? "default" : "secondary"}
                  className="text-[10px] shrink-0"
                >
                  {selectedAffiliate.status === "active" ? "Ativo" : selectedAffiliate.status}
                </Badge>
              </div>
            ) : (
              <span className="text-muted-foreground">Selecione um afiliado...</span>
            )}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[350px] p-0" align="start">
          <Command>
            <CommandInput placeholder="Buscar afiliado..." />
            <CommandList>
              <CommandEmpty>Nenhum afiliado encontrado.</CommandEmpty>
              <CommandGroup heading="Visão consolidada">
                <CommandItem
                  value="todos os afiliados consolidado"
                  onSelect={() => {
                    setSelectedId(selectedId === ALL_AFFILIATES_ID ? undefined : ALL_AFFILIATES_ID);
                    setOpen(false);
                  }}
                >
                  <Check className={`mr-2 h-4 w-4 ${selectedId === ALL_AFFILIATES_ID ? "opacity-100" : "opacity-0"}`} />
                  <Users className="mr-2 h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Todos os afiliados</span>
                </CommandItem>
              </CommandGroup>
              <CommandGroup heading={`${affiliates.length} afiliados disponíveis`}>
                {affiliates.map((affiliate) => (
                  <CommandItem
                    key={affiliate.id}
                    value={`${affiliate.name} ${affiliate.email}`}
                    onSelect={() => {
                      setSelectedId(affiliate.id === selectedId ? undefined : affiliate.id);
                      setOpen(false);
                    }}
                  >
                    <Check
                      className={`mr-2 h-4 w-4 ${
                        selectedId === affiliate.id ? "opacity-100" : "opacity-0"
                      }`}
                    />
                    <div className="flex flex-col min-w-0 flex-1">
                      <span className="text-sm font-medium truncate">{affiliate.name}</span>
                      <span className="text-xs text-muted-foreground truncate">{affiliate.email}</span>
                      {affiliate.manager_name && (
                        <span className="text-[10px] text-muted-foreground/70 truncate">
                          Gestor: {affiliate.manager_name}
                        </span>
                      )}
                    </div>
                    <Badge
                      variant={affiliate.status === "active" ? "default" : "secondary"}
                      className="ml-auto text-[10px] shrink-0"
                    >
                      {affiliate.status === "active" ? "Ativo" : affiliate.status}
                    </Badge>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {(selectedAffiliate || selectedId === ALL_AFFILIATES_ID) && (
        <Button
          variant="ghost"
          size="sm"
          className="shrink-0 text-xs text-muted-foreground"
          onClick={() => setSelectedId(undefined)}
        >
          Limpar
        </Button>
      )}
    </div>
  );
}
